import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { MultilingualInput, emptyMultilingual } from "../../components/common/MultilingualInput";
import { asMultilingual } from "../../lib/multilingual-helpers";
import { useToast } from "../../components/ui/use-toast";
import {
  createCategory,
  updateCategory,
} from "../../redux/slices/categorySlice";

function CategoryDialog({ open, onOpenChange, onSaved, category, categories }) {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: emptyMultilingual(),
    description: emptyMultilingual(),
    parent_category_id: null,
    is_active: true,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (category) {
      setFormData({
        name: asMultilingual(category.name),
        description: asMultilingual(category.description),
        parent_category_id: category.parent_category_id || null,
        is_active: category.is_active !== false,
      });
    } else {
      setFormData({
        name: emptyMultilingual(),
        description: emptyMultilingual(),
        parent_category_id: null,
        is_active: true,
      });
    }
  }, [category, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.en) {
      toast({
        title: "Validation Error",
        description: "English name is required",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      if (category) {
        await dispatch(
          updateCategory({ id: category.id, data: formData })
        ).unwrap();
        toast({
          title: "Success",
          description: "Category updated successfully",
        });
      } else {
        await dispatch(createCategory(formData)).unwrap();
        toast({
          title: "Success",
          description: "Category created successfully",
        });
      }
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      toast({
        title: "Error",
        description: error || "Failed to save category",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const availableParents = categories.filter((cat) => cat.id !== category?.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {category ? "Edit Category" : "Create Category"}
          </DialogTitle>
          <DialogDescription>
            {category
              ? "Update category information"
              : "Add a new category to organize questions"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <MultilingualInput
            label="Category Name"
            value={formData.name}
            onChange={(value) => setFormData({ ...formData, name: value })}
            placeholder="Enter category name"
            required
            compact
          />

          <MultilingualInput
            label="Description"
            value={formData.description}
            onChange={(value) =>
              setFormData({ ...formData, description: value })
            }
            placeholder="Enter category description"
            multiline
            rows={3}
          />

          <div className="space-y-2">
            <Label>Parent Category (Optional)</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
              value={formData.parent_category_id || ""}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  parent_category_id: e.target.value || null,
                })
              }
            >
              <option value="">None (Root Category)</option>
              {availableParents.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name?.en || cat.name}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Select a parent to create a subcategory
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Active Status</Label>
              <p className="text-xs text-muted-foreground">
                Inactive categories won't be visible to users
              </p>
            </div>
            <Switch
              checked={formData.is_active}
              onCheckedChange={(checked) =>
                setFormData({ ...formData, is_active: checked })
              }
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : category ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default CategoryDialog;
